import { Link } from 'react-router-dom';
import { Notice } from '@kollez/design-system';
import { PageMeta } from '@/components/PageMeta';

/**
 * /status — placeholder status page. Real live status ships when the
 * status.kollez.ai subdomain lands on Instatus / Statuspage / BetterUptime
 * (owned by James). Until then, this page tells the reader where to go
 * and what to expect.
 */
export function StatusPage() {
  const components: Array<{ name: string; status: 'operational' }> = [
    { name: 'kollez.ai · marketing',        status: 'operational' },
    { name: 'api.kollez.ai · public',       status: 'operational' },
    { name: 'api.kollez.ai · authenticated', status: 'operational' },
    { name: 'Nexie · AI',                    status: 'operational' },
    { name: 'Notifications · email + SMS',   status: 'operational' },
  ];

  return (
    <>
      <PageMeta
        title="Status"
        description="Real-time status of Kollez services and incident history."
        canonical="/status"
      />

      <section className="mx-auto max-w-page-narrow px-5 py-16 lg:px-10 lg:py-24 grid gap-6">
        <div>
          <p className="eyebrow">System status</p>
          <h1 className="display text-ink-primary mt-2">All systems operational.</h1>
          <p className="mt-4 text-lg leading-8 text-ink-secondary">
            Kollez targets 99.9% uptime for all customer-facing services. The live
            status dashboard is being brought up at{' '}
            <a className="link" href="https://status.kollez.ai">status.kollez.ai</a>{' '}
            — until then, this page reflects the current operational picture.
          </p>
        </div>

        <div className="card overflow-hidden">
          <div className="flex items-center justify-between border-b border-line-tertiary px-5 py-3">
            <span className="text-sm font-medium">Component</span>
            <span className="text-sm font-medium">Current status</span>
          </div>
          <ul>
            {components.map((c) => (
              <li key={c.name} className="flex items-center justify-between px-5 py-3 border-b border-line-tertiary last:border-b-0">
                <span>{c.name}</span>
                <span className="flex items-center gap-2 text-sm" style={{ color: 'rgb(var(--ink-success))' }}>
                  <span
                    className="dot dot-success"
                    style={{ width: 8, height: 8, borderRadius: '50%', background: 'currentColor' }}
                    aria-hidden="true"
                  />
                  Operational
                </span>
              </li>
            ))}
          </ul>
        </div>

        <Notice tone="info">
          Subscribe to incident updates by emailing{' '}
          <a className="link" href="mailto:status-subscribe@kollez.ai">status-subscribe@kollez.ai</a>{' '}
          — you'll get email alerts when a component's status changes. RSS + Slack
          integrations arrive with the full status.kollez.ai dashboard.
        </Notice>

        <p className="helper text-center mt-2">
          Reporting a problem?{' '}
          <Link className="link" to="/contact?topic=support">Contact support</Link>{' '}
          or email <a className="link" href="mailto:support@kollez.ai">support@kollez.ai</a>.
        </p>
      </section>
    </>
  );
}
